import { faExclamationTriangle, faFileInvoiceDollar, faLocationArrow, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import apiMethods from '../../constants/apiMethods';
import alertMessages from '../../helpers/alertMessages';
import api from '../../helpers/api';

import es from '../../lang/es';

const PaymentForm = props => {
    //Tomo los props
    const { data, setData, jurisdiction } = props;
    const { show, proceeding, paymentType } = data;
    //Inicializo las variables
    const initialPayment = {
        type: '',
        amount: '',
        readOnly: false
    }
    //Inicializo los ref
    const typeRef = useRef();
    const amountRef = useRef();
    //Inicializo el state local
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ payment, setPayment ] = useState(initialPayment)
    const [ showAlert, setShowAlert ] = useState(false);
    //Menejo los cambios
    const handleChange = e => {
        setPayment({
            ...payment,
            [e.target.name]: e.target.value
        })
        //Me fijo si cambia el tipo
        if(e.target.name === 'type'){
            //Busco el tipo seleccionado
            const t = paymentType.types.find(element => element.id === parseInt(e.target.value));
            //Me fijo si existe
            if(t){
                //Me fijo si tiene monto fijo
                if(t.use_amount){
                    //Seteo el monto
                    setPayment({
                        ...payment,
                        [e.target.name]: e.target.value,
                        type: t.id,
                        amount: t.amount,
                        readOnly: t.use_amount
                    })
                }else{
                    //Habilito campo
                    setPayment({
                        ...payment,
                        [e.target.name]: e.target.value,
                        amount: '',
                        readOnly: false
                    })
                    //Pongo el foco
                    amountRef.current.focus();
                }
                //Me fijo si debe mostrar alerta
                if(t.id === 31){ //Sólo en ese se muestra alerta por ahora
                    setShowAlert(true);
                }else{
                    setShowAlert(false);
                }
            }
        }
    }
    //Manejo el guardado
    const handleSavePayment = async e => {
        e.preventDefault();
        //Reinicio los errores
        setError(null);
        //Valido los datos
        if(!payment.type || payment.type === '' || payment.type <= 0){
            typeRef.current.focus();
            return false;
        }
        if(!payment.amount || payment.amount === '' || payment.amount <= 0 || isNaN(payment.amount)){
            amountRef.current.focus();
            return false;
        }
        setLoading(true);
        //Tomo datos del proceso
        const pData = {
            procid: proceeding.procid,
            number: proceeding.nro_expediente,
            cover: `${proceeding.acto} C/ ${proceeding.dema} S/ ${proceeding.tipo_proceso}`,
            codigo: proceeding.codb,
            jurisdiction_id: jurisdiction,
            unit_id: proceeding.juzgid,
            unit: proceeding.juzgado.dscr
        };

        //Seteo los parámetros
        const saveData = {
            ...payment,
            proceeding: pData
        }
        //Ejecuto el guardar
        try{
            await api.post(apiMethods.PAYMENTS, saveData);
            //Cierro la venta
            handleClose();
            //Mensaje
            alertMessages.success(es.ACTIONS.SAVED);
        }catch(error){
            const errorResponse = error.data;
            //Verifico si hay error
            if(errorResponse){
                setError(errorResponse);
            }
            //Habilito los campos
            setLoading(false);
        }
    }
    //Manejo el cierre
    const handleClose = () => {
        //Reinicio los datos
        setData({
            show: false,
            proceeding: null,
            paymentType: null
        });
        //Reinicio los datos
        setPayment(initialPayment);
        setLoading(false);
        setError(null);
    }
    //Render
    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <h4 className="modal-title">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
                    <strong className="mr-2">{ paymentType ? paymentType.name : null }</strong>
                    <br />
                    <small>
                        { 
                            proceeding ? (
                                `${proceeding.nro_expediente} | ${proceeding.juzgado.dscr}`
                            ) : null
                        }
                    </small>
                </h4>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSavePayment}>
                    <Row>
                        <Col xs={12}>
                            <Alert variant="info">
                                Seleccione el <strong>Tipo de Pago</strong>, y luego ingrese el <strong>Monto</strong> a pagar. Por último presione <FontAwesomeIcon icon={faLocationArrow} /> <strong>AGREGAR PAGO</strong>.
                            </Alert>
                            {
                                error ? (
                                    <Alert className="payment-error" variant="danger">
                                        <b>Error:</b> { error.message ? error.message : es.ERRORS.DEFAULT }
                                    </Alert>
                                ) : null
                            }
                        </Col>
                        <Col xs={12}>
                            <Form.Group>
                                <Form.Control
                                    as="select"
                                    className="pago-tipo"
                                    name="type"
                                    required
                                    disabled={loading}
                                    onChange={handleChange}
                                    value={payment.type}
                                    ref={typeRef}
                                >
                                    <option value="">Seleccione el Tipo de { paymentType && paymentType.name }</option>
                                    {
                                        paymentType && paymentType.types ? (
                                            paymentType.types.map( type => (
                                                <option 
                                                    key={`st${type.id}`}
                                                    value={ type.id } 
                                                >
                                                    { type.description }
                                                </option>
                                            ))
                                        ) : null
                                    }
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        {
                            showAlert ? (
                                <Col xs={12}>
                                    <Alert variant="warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} /> <strong>ATENCIÓN: Se debe pagar individualmente cada manda judicial</strong>
                                    </Alert>
                                </Col>
                            ) : null
                        }
                        <Col md={6} xs={12} className="form-inline mb-2">
                            <Form.Group>
                                <Form.Label className="mr-2">
                                    <strong>{ es.FIELDS.AMOUNT.toUpperCase() }: { es.PAYMENTS.CURRENCY }</strong>
                                </Form.Label>
                                <Form.Control
                                    className="pago-monto on"
                                    type="number"
                                    name="amount"
                                    value={ payment.amount }
                                    onChange={handleChange}
                                    ref={amountRef}
                                    required
                                    disabled={loading}
                                    readOnly={payment.readOnly}
                                />
                                {
                                    loading ? (
                                        <Spinner animation="border" className="mt-3 mt-md-0 ml-2" role="status">
                                            <span className="sr-only">{ es.SAVING }...</span>
                                        </Spinner>
                                    ) : (
                                        <Button className="mt-3 mt-md-0 col-xs-8 ml-md-2" variant="success" type="submit">
                                            <FontAwesomeIcon icon={faLocationArrow} /> <strong>{ es.PAYMENTS.SAVE.toUpperCase() }</strong>
                                        </Button>
                                    )
                                }
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <div className="text-muted">
                                <i>Ingrese sólo números y punto (no coma) para decimales (hasta 2).</i>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="danger" onClick={handleClose}>
                    <FontAwesomeIcon icon={faTimes} /> <strong>{ es.CANCEL.toUpperCase() }</strong>
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentForm;